import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import QRCode from 'qrcode';
import { QidUser, resolveUrl, authHeaders } from './api.ts';
import { useThemeMode, getTheme, FONT_UI } from './theme';
import { isNativeApp, nativePlatform, nativePrintAvailable, printPdf, showPdf } from './nativePrint';
import { SHEET_MARGIN_MM, sheetPdfPages, printerPdfPages } from './labelPdf';
import { renderLabelPdf } from './labelCanvas';
import {
  CUT_LIST, DEFAULT_CUT_COUNTS, MINCE, STEW, cutSequence, reconcileLabels,
  type PrintOutput, type SheetFormat, DEFAULT_SHEET, SHEET_PRESETS, PRINTER_SIZES,
  chunk, clampPan, zoomAround, MM_TO_PX, labelPricing, labelQrUrl, changedSince, publicNotes,
} from './labelLayout';

// ─── Data constants ───────────────────────────────────────────────────────────
const VILT_SPECIES = [
  'Älg','Dovhjort','Kronhjort','Rådjur','Råbock','Vildsvin',
  'Björn','Ren','Hare','And','Grågås','Kanadagås','Tjäder','Orre','Fasan','Muflon',
];

const STYCKDETALJER = [
  'Innanlår','Ytterlår','Stek','Rostbiff','Rulle','Ryggbiff',
  'Filé','Bogstek','Sida','Grytbitar','Färs','Fransyska',
  'Hjärta','Lever','Njure','Hals','Revbensspjäll','Hel kropp',
];

export const DEFAULT_VILT_CUTS: string[] = [
  'Innanlår','Innanlår',
  'Ytterlår','Ytterlår',
  'Stek','Stek',
  'Rostbiff','Rostbiff',
  'Rulle','Rulle',
  'Ryggbiff','Ryggbiff',
  'Filé','Filé',
  'Bogstek','Bogstek',
  'Sida','Sida',
  'Grytbitar','Grytbitar','Grytbitar','Grytbitar','Grytbitar',
  'Färs','Färs','Färs','Färs','Färs',
];

// Egna styckdelar (fritext) sparas lokalt så de dyker upp i listan nästa gång
const CUSTOM_CUTS_KEY = 'qvenari_custom_cuts';

function loadCustomCuts(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(CUSTOM_CUTS_KEY) || '[]');
    return Array.isArray(v) ? v.filter((s): s is string => typeof s === 'string' && !!s.trim()) : [];
  } catch { return []; }
}

function saveCustomCuts(cuts: string[]) {
  try { localStorage.setItem(CUSTOM_CUTS_KEY, JSON.stringify(cuts)); } catch { /* ignore */ }
}

const UTRUSTNING_KATEGORIER = [
  'Gevär','Pistol','Hagelvapen','Pil och båge','Kniv','Kikare','Kläder','Tillbehör',
];

const SPECIES_EMOJIS: Record<string, string> = {
  'Älg':'🦌','Dovhjort':'🦌','Kronhjort':'🦌','Rådjur':'🦌','Råbock':'🦌',
  'Vildsvin':'🐗','Björn':'🐻','Ren':'🦌','Hare':'🐇','And':'🦆',
  'Grågås':'🪿','Kanadagås':'🪿','Tjäder':'🐦','Orre':'🐦','Fasan':'🐦','Muflon':'🐏',
};

const EQUIP_EMOJIS: Record<string, string> = {
  'Gevär':'🔫','Pistol':'🔫','Hagelvapen':'🔫','Pil och båge':'🏹',
  'Kniv':'🔪','Kikare':'🔭','Kläder':'🧥','Tillbehör':'⚙️',
};

declare const __APP_BUILD_ID__: string;
const APP_BUILD_ID = typeof __APP_BUILD_ID__ !== 'undefined' ? __APP_BUILD_ID__ : 'dev';

// A4 (297 mm) minus 5 mm marginal uppe och nere
const PAGE_H_MM = 287;
const LABEL_H_MM = 55;

// ─── Types ────────────────────────────────────────────────────────────────────
export interface LabelData {
  id: string;
  species: string;
  cut: string;
  markName: string;
  skjutetDate: string;
  styckatDate: string;
  weightKg: string;
  kgPrice: string;
  autoPrice: boolean;
  totalPrice: string;
  qrValue: string;
  notes: string;
  // Custom image (uploaded per label)
  customImageUrl?: string;
  imgX: number;    // 0–100 % from left (center of image)
  imgY: number;    // 0–100 % from top  (center of image)
  imgScale: number; // size multiplier
  copies?: number;  // antal utskrivna exemplar av etiketten (default 1)
  viltRegId?: string; // ET7: registrerat djur (spårbarhet när QR-koden skannas)
}

export interface LabelPrinterProps {
  user: QidUser | null;
  isAdmin: boolean;
  defaultMarkName?: string;
  embedded?: boolean;
  onClose?: () => void;
  preloadedLabels?: LabelData[];
  initialTemplateType?: TemplateType;
}

type Step = 'select' | 'edit' | 'amounts' | 'cuts' | 'preview';
type TemplateType = 'vilt' | 'utrustning';

// ─── UUID helper ──────────────────────────────────────────────────────────────
function uuidv4(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// ─── Label generator ──────────────────────────────────────────────────────────
export function generateLabels(
  rows: number,
  cols: number,
  species: string,
  cut: string,
  markName: string,
  cutsSequence?: string[],
): LabelData[] {
  const total = rows * cols;
  return Array.from({ length: total }, (_, i) => {
    const id = uuidv4();
    const assignedCut = cutsSequence
      ? (cutsSequence[i] ?? cutsSequence[cutsSequence.length - 1] ?? cut)
      : cut;
    return {
      id,
      species,
      cut: assignedCut,
      markName,
      skjutetDate: '',
      styckatDate: '',
      weightKg: '',
      kgPrice: '',
      autoPrice: true,
      totalPrice: '',
      qrValue: labelQrUrl(id),
      notes: '',
      customImageUrl: '',
      imgX: 82,
      imgY: 32,
      imgScale: 1,
      copies: 1,
    };
  });
}

// ─── Etikettens färger ────────────────────────────────────────────────────────
// ET1: etiketten är alltid ljus – samma på skärmen i mörkt läge som på papperet.
const LBL = {
  bg: '#ffffff', ink: '#1d261d', sub: '#3f473c', muted: '#6b7166',
  accent: '#a4560a', border: '#c9c1ae', field: '#f4f1ea', qrDark: '#1a2a1a',
};

const Q_LOGO_SRC = '/qvenari-logo.png';

/** Priset som sparas och skrivs ut: uträknat (kilopris × vikt) eller det angivna. */
function labelTotal(label: LabelData, weightByHand: boolean): string {
  return labelPricing(label, weightByHand).price.text;
}

// ─── QR-kod med Q-loggan i mitten (2× upplösning) ─────────────────────────────
function QRWithLogo({ value, size }: { value: string; size: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const physSize = size * 2; // 2× for sharp rendering

  useEffect(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    let alive = true;

    QRCode.toCanvas(canvas, value || 'https://qvenari.com', {
      width: physSize,
      margin: 1,
      color: { dark: LBL.qrDark, light: '#ffffff' },
      errorCorrectionLevel: 'H',
    }).then(() => {
      const ctx = canvas.getContext('2d');
      if (!ctx || !alive) return;

      const cx = physSize / 2;
      const cy = physSize / 2;
      const r  = physSize * 0.15; // white circle radius

      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fillStyle = '#ffffff';
      ctx.fill();

      // Q-loggan är inte kvadratisk – behåll proportionerna
      const img = new Image();
      img.onload = () => {
        if (!alive) return;
        const h = r * 1.3;
        const w = h * (img.naturalWidth / (img.naturalHeight || 1));
        ctx.drawImage(img, cx - w / 2, cy - h / 2, w, h);
      };
      img.src = Q_LOGO_SRC;
    }).catch(console.error);
    return () => { alive = false; };
  }, [value, physSize]);

  return (
    <canvas
      ref={canvasRef}
      width={physSize}
      height={physSize}
      style={{ width: size, height: size, borderRadius: 2, display: 'block' }}
    />
  );
}

// ─── Single printable label (vilt) ───────────────────────────────────────────
// ET3: styckningsdag (inte skjutdatum) och en stor ruta för vikten.
interface LabelCardProps {
  label: LabelData;
  /** Bara bilden (flytta/skala) redigeras på kortet; texterna i formuläret bredvid. */
  editing: boolean;
  onChange: (updated: LabelData) => void;
  onClick?: () => void;
  printMode?: boolean;
  weightByHand?: boolean;
}

export function LabelCard({ label, editing, onChange, onClick, printMode = false, weightByHand = false }: LabelCardProps) {
  const cardRef    = useRef<HTMLDivElement>(null);
  const fileRef    = useRef<HTMLInputElement>(null);
  // Drag state (ref so no re-render jitter)
  const dragRef   = useRef<{ px: number; py: number; ix: number; iy: number } | null>(null);
  const resizeRef = useRef<{ py: number; scale: number } | null>(null);

  const pricing = labelPricing(label, weightByHand);
  const qrSize  = printMode ? 62 : 66;
  const imgX    = label.imgX    ?? 82;
  const imgY    = label.imgY    ?? 32;
  const imgScale = label.imgScale ?? 1;
  const imgW    = 32 * imgScale; // % of card width

  const cardStyle: React.CSSProperties = {
    position: 'relative',
    width: printMode ? '100%' : 340,
    height: printMode ? '100%' : 210,
    background: LBL.bg,
    color: LBL.ink,
    // Streckad kant bara på skärmen – döljs vid utskrift via .label-card
    border: `2px dashed ${editing ? '#e5882a' : LBL.border}`,
    borderRadius: printMode ? 1 : 4,
    overflow: 'hidden',
    cursor: onClick ? 'pointer' : 'default',
    boxSizing: 'border-box',
    fontFamily: FONT_UI,
  };

  function upd(patch: Partial<LabelData>) { onChange({ ...label, ...patch }); }

  function onImgPointerDown(e: React.PointerEvent) {
    e.preventDefault(); e.stopPropagation();
    if (!editing) return;
    dragRef.current = { px: e.clientX, py: e.clientY, ix: imgX, iy: imgY };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }
  function onImgPointerMove(e: React.PointerEvent) {
    if (!dragRef.current || !cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const dx = ((e.clientX - dragRef.current.px) / rect.width)  * 100;
    const dy = ((e.clientY - dragRef.current.py) / rect.height) * 100;
    upd({
      imgX: Math.max(5, Math.min(95, dragRef.current.ix + dx)),
      imgY: Math.max(5, Math.min(95, dragRef.current.iy + dy)),
    });
  }
  function onImgPointerUp() { dragRef.current = null; }

  function onResizePointerDown(e: React.PointerEvent) {
    e.preventDefault(); e.stopPropagation();
    resizeRef.current = { py: e.clientY, scale: imgScale };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }
  function onResizePointerMove(e: React.PointerEvent) {
    if (!resizeRef.current) return;
    const dy = e.clientY - resizeRef.current.py;
    upd({ imgScale: Math.max(0.25, Math.min(5, resizeRef.current.scale + dy * 0.015)) });
  }
  function onResizePointerUp() { resizeRef.current = null; }

  function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => upd({ customImageUrl: ev.target?.result as string, imgX: 82, imgY: 32, imgScale: 1 });
    reader.readAsDataURL(file);
    e.target.value = ''; // reset so same file can re-trigger
  }

  const caps: React.CSSProperties = { color: LBL.muted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em' };

  return (
    <div>
    <div ref={cardRef} style={cardStyle} className="label-card" data-label-id={label.id} onClick={onClick}>

      {label.customImageUrl && (
        <div
          style={{
            position: 'absolute',
            left: `${imgX}%`, top: `${imgY}%`,
            transform: 'translate(-50%, -50%)',
            width: `${imgW}%`,
            zIndex: editing ? 4 : 1,
            cursor: editing ? 'move' : 'inherit',
            userSelect: 'none',
            touchAction: editing ? 'none' : undefined,
          }}
          onPointerDown={editing ? onImgPointerDown : undefined}
          onPointerMove={editing ? onImgPointerMove : undefined}
          onPointerUp={editing   ? onImgPointerUp   : undefined}
        >
          <img
            src={label.customImageUrl}
            draggable={false}
            style={{ width: '100%', height: 'auto', display: 'block', borderRadius: 2, opacity: editing ? 0.85 : 0.65, pointerEvents: 'none' }}
            alt=""
          />
          {editing && (
            <div
              style={{ position: 'absolute', bottom: -6, right: -6, width: 14, height: 14, background: '#e5882a', border: '2px solid #fff', borderRadius: 1, cursor: 'se-resize', zIndex: 5 }}
              onPointerDown={onResizePointerDown}
              onPointerMove={onResizePointerMove}
              onPointerUp={onResizePointerUp}
            />
          )}
        </div>
      )}

      <div style={{ position: 'relative', zIndex: 2, padding: printMode ? '7px 9px' : '9px 11px', height: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 5 }}>

        {/* Art, styckdetalj, mark + QR */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: LBL.accent, fontSize: 14, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', lineHeight: 1.15 }}>
              {label.species || '—'}
            </div>
            <div style={{ color: LBL.ink, fontSize: 24, fontWeight: 800, lineHeight: 1.1, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {label.cut || '—'}
            </div>
            {label.markName && (
              <div style={{ color: LBL.sub, fontSize: 13, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {label.markName}
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 3 }}>
              <span style={caps}>Styckat</span>
              {label.styckatDate
                ? <span style={{ color: LBL.ink, fontSize: 14, fontWeight: 600 }}>{label.styckatDate}</span>
                : <span style={{ display: 'inline-block', width: 90, borderBottom: `1.5px solid ${LBL.ink}`, height: 12 }} />}
            </div>
          </div>
          <div style={{ flexShrink: 0 }}>
            <QRWithLogo value={label.qrValue || 'https://qvenari.com'} size={qrSize} />
          </div>
        </div>

        {/* Kilopris, vikt och pris – tre egna rutor; tomma döljs eller blir rutor att skriva i (ET5) */}
        <div style={{ flex: 1, minHeight: 44, display: 'flex', gap: 5 }}>
          {([
            ['kgPrice', 'Kilopris', pricing.kgPrice, 'kr/kg', 1],
            ['weight', 'Vikt', pricing.weight, 'kg', 1.4],
            ['price', 'Pris', pricing.price, 'kr', 1.1],
          ] as const).filter(([, , f]) => f.mode !== 'hidden').map(([key, caption, f, unit, grow]) => (
            <div key={key} data-field={key}
              style={{ flex: grow, minWidth: 0, border: `${key === 'weight' ? 2 : 1.5}px solid ${LBL.ink}`, borderRadius: 4, padding: '2px 6px 3px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxSizing: 'border-box' }}>
              <span style={{ ...caps, lineHeight: 1.2 }}>{caption}</span>
              <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 3 }}>
                <span style={{ color: LBL.ink, fontSize: key === 'weight' ? 22 : 18, fontWeight: 800, lineHeight: 1.15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.text}</span>
                <span style={{ color: LBL.ink, fontSize: key === 'weight' ? 14 : 11, fontWeight: 700, lineHeight: 1, whiteSpace: 'nowrap' }}>{unit}</span>
              </div>
            </div>
          ))}
        </div>

        {label.notes && (
          <div style={{ fontSize: 12, fontStyle: 'italic', color: LBL.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label.notes}</div>
        )}
      </div>

    </div>
      {editing && (
        <div className="no-print" style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 6 }}>
          <button type="button" onClick={e => { e.stopPropagation(); fileRef.current?.click(); }}
            style={{ background: '#fff', border: `1px solid ${LBL.border}`, borderRadius: 4, color: LBL.ink, fontSize: 13, fontWeight: 700, padding: '6px 12px', cursor: 'pointer', fontFamily: 'inherit' }}>
            {label.customImageUrl ? 'Byt bild' : '+ Bild'}
          </button>
          {label.customImageUrl && (
            <button type="button" onClick={e => { e.stopPropagation(); upd({ customImageUrl: '' }); }} aria-label="Ta bort bilden"
              style={{ background: '#fff', border: `1px solid ${LBL.border}`, borderRadius: 4, color: LBL.ink, fontSize: 12, padding: '3px 7px', cursor: 'pointer', fontFamily: 'inherit' }}>
              ✕
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleUpload} />
        </div>
      )}
    </div>
  );
}

// ─── Equipment label card ─────────────────────────────────────────────────────
interface EquipLabelCardProps {
  label: LabelData;
  editing: boolean;
  onChange: (updated: LabelData) => void;
  onFillAll?: () => void;
  onClick?: () => void;
  printMode?: boolean;
}

function EquipLabelCard({ label, editing, onChange, onFillAll, onClick, printMode = false }: EquipLabelCardProps) {
  const fileRef   = useRef<HTMLInputElement>(null);
  const cardRef   = useRef<HTMLDivElement>(null);
  const dragRef   = useRef<{ px: number; py: number; ix: number; iy: number } | null>(null);
  const resizeRef = useRef<{ py: number; scale: number } | null>(null);

  const labelW = printMode ? '100%' : '340px';
  const labelH = printMode ? '100%' : '233px';
  const qrSize = printMode ? 66 : 74;
  const imgX   = label.imgX    ?? 82;
  const imgY   = label.imgY    ?? 32;
  const imgScale = label.imgScale ?? 1;
  const imgW   = 32 * imgScale;

  const cardStyle: React.CSSProperties = {
    position: 'relative',
    width: labelW, height: labelH,
    background: LBL.bg, color: LBL.ink,
    border: `2px dashed ${editing ? LBL.accent : LBL.border}`,
    borderRadius: printMode ? 1 : 4,
    overflow: 'hidden',
    cursor: editing ? 'default' : 'pointer',
    boxSizing: 'border-box',
    fontFamily: FONT_UI,
    transition: 'border-color 0.2s',
  };

  const inputStyle: React.CSSProperties = {
    background: editing ? LBL.field : 'transparent',
    border: editing ? `1px solid ${LBL.border}` : 'none',
    borderRadius: 2, color: LBL.ink, fontSize: 20,
    padding: editing ? '2px 5px' : '0',
    outline: 'none', fontFamily: 'inherit', width: '100%',
  };

  function upd(patch: Partial<LabelData>) { onChange({ ...label, ...patch }); }

  function onImgPointerDown(e: React.PointerEvent) {
    e.preventDefault(); e.stopPropagation();
    if (!editing) return;
    dragRef.current = { px: e.clientX, py: e.clientY, ix: imgX, iy: imgY };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }
  function onImgPointerMove(e: React.PointerEvent) {
    if (!dragRef.current || !cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const dx = ((e.clientX - dragRef.current.px) / rect.width)  * 100;
    const dy = ((e.clientY - dragRef.current.py) / rect.height) * 100;
    upd({ imgX: Math.max(5, Math.min(95, dragRef.current.ix + dx)), imgY: Math.max(5, Math.min(95, dragRef.current.iy + dy)) });
  }
  function onImgPointerUp() { dragRef.current = null; }

  function onResizePointerDown(e: React.PointerEvent) {
    e.preventDefault(); e.stopPropagation();
    resizeRef.current = { py: e.clientY, scale: imgScale };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }
  function onResizePointerMove(e: React.PointerEvent) {
    if (!resizeRef.current) return;
    const dy = e.clientY - resizeRef.current.py;
    upd({ imgScale: Math.max(0.25, Math.min(5, resizeRef.current.scale + dy * 0.015)) });
  }
  function onResizePointerUp() { resizeRef.current = null; }

  function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => upd({ customImageUrl: ev.target?.result as string, imgX: 82, imgY: 32, imgScale: 1 });
    reader.readAsDataURL(file);
    e.target.value = '';
  }

  return (
    <div ref={cardRef} style={cardStyle} className="label-card" onClick={!editing ? onClick : undefined}>

      {/* Accent bar */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(to right, ${LBL.accent}, ${'#16a594'})` }} />

      {/* Custom image */}
      {label.customImageUrl && (
        <div
          style={{ position: 'absolute', left: `${imgX}%`, top: `${imgY}%`, transform: 'translate(-50%, -50%)', width: `${imgW}%`, zIndex: editing ? 4 : 1, cursor: editing ? 'move' : 'default', userSelect: 'none' }}
          onPointerDown={editing ? onImgPointerDown : undefined}
          onPointerMove={editing ? onImgPointerMove : undefined}
          onPointerUp={editing   ? onImgPointerUp   : undefined}
        >
          <img src={label.customImageUrl} draggable={false} style={{ width: '100%', height: 'auto', display: 'block', borderRadius: 2, opacity: editing ? 0.85 : 0.65, pointerEvents: 'none' }} alt="" />
          {editing && (
            <div style={{ position: 'absolute', bottom: -6, right: -6, width: 14, height: 14, background: LBL.accent, border: '2px solid #fff', borderRadius: 1, cursor: 'se-resize', zIndex: 5 }}
              onPointerDown={onResizePointerDown} onPointerMove={onResizePointerMove} onPointerUp={onResizePointerUp} />
          )}
        </div>
      )}

      <div style={{ position: 'relative', zIndex: 2, padding: printMode ? '8px 8px 5px' : '14px 12px 10px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1, paddingRight: 8 }}>
            {editing ? (
              <select value={label.species} onChange={e => upd({ species: e.target.value })}
                style={{ background: LBL.field, border: `1px solid ${LBL.border}`, borderRadius: 2, color: '#0a6fb0', fontSize: 28, fontWeight: 800, fontFamily: 'inherit', outline: 'none', padding: '2px 4px', cursor: 'pointer' }}>
                {UTRUSTNING_KATEGORIER.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            ) : (
              <div style={{ color: '#0a6fb0', fontSize: printMode ? 26 : 30, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                {EQUIP_EMOJIS[label.species] || '⚙️'} {label.species || '—'}
              </div>
            )}

            {editing ? (
              <input type="text" value={label.cut} onChange={e => upd({ cut: e.target.value })} placeholder="Namn på utrustning"
                style={{ ...inputStyle, marginTop: 4, fontSize: 24, fontWeight: 600 }} />
            ) : (
              <div style={{ color: LBL.ink, fontSize: printMode ? 20 : 24, marginTop: 3, fontWeight: 600 }}>{label.cut || '—'}</div>
            )}

            {editing ? (
              <input type="text" value={label.markName} onChange={e => upd({ markName: e.target.value })} placeholder="Ägare / Jaktlag"
                style={{ ...inputStyle, marginTop: 3, fontSize: 20, color: LBL.sub }} />
            ) : (
              <div style={{ color: LBL.sub, fontSize: 20, marginTop: 2 }}>{label.markName || 'Ägare okänd'}</div>
            )}
          </div>

          <div style={{ flexShrink: 0 }}>
            <QRWithLogo value={label.qrValue || 'https://qvenari.se'} size={qrSize} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 4, alignItems: 'center' }}>
          <span style={{ color: LBL.muted, fontSize: 16, textTransform: 'uppercase', letterSpacing: '0.06em' }}>ID</span>
          <span style={{ color: LBL.sub, fontSize: 18, fontFamily: 'monospace' }}>{label.id.slice(0, 8).toUpperCase()}</span>
        </div>

        {editing ? (
          <input type="text" value={label.notes} onChange={e => upd({ notes: e.target.value })} placeholder="Serienummer, inköpsdatum, anteckningar..."
            style={{ ...inputStyle, marginTop: 4, fontSize: 18, color: LBL.sub }} />
        ) : (
          label.notes ? <div style={{ color: LBL.muted, fontSize: 18, marginTop: 2, fontStyle: 'italic' }}>{label.notes}</div> : null
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
          <span style={{ color: LBL.muted, fontSize: 16, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Värde</span>
          {editing ? (
            <>
              <input type="text" value={label.totalPrice} onChange={e => upd({ totalPrice: e.target.value })} placeholder="—" style={{ ...inputStyle, width: 60 }} />
              <span style={{ color: LBL.muted, fontSize: 16 }}>kr</span>
            </>
          ) : (
            <span style={{ color: LBL.accent, fontSize: 20, fontWeight: 700 }}>{label.totalPrice ? `${label.totalPrice} kr` : '—'}</span>
          )}
        </div>

        {editing && (
          <div style={{ display: 'flex', gap: 4, marginTop: 4, alignItems: 'center' }} className="no-print">
            <button onClick={e => { e.stopPropagation(); fileRef.current?.click(); }}
              style={{ background: label.customImageUrl ? '#fbeedd' : LBL.field, border: `1px solid ${label.customImageUrl ? LBL.accent : LBL.border}`, borderRadius: 2, color: label.customImageUrl ? LBL.accent : LBL.sub, fontSize: 18, fontWeight: 600, padding: '2px 8px', cursor: 'pointer', fontFamily: 'inherit' }}>
              {label.customImageUrl ? '🖼 Byt bild' : '+ Bild'}
            </button>
            {label.customImageUrl && (
              <button onClick={e => { e.stopPropagation(); upd({ customImageUrl: '' }); }}
                style={{ background: 'transparent', border: `1px solid ${LBL.border}`, borderRadius: 2, color: LBL.muted, fontSize: 18, padding: '2px 6px', cursor: 'pointer', fontFamily: 'inherit' }}>✕</button>
            )}
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleUpload} />
            {onFillAll && (
              <button onClick={e => { e.stopPropagation(); onFillAll(); }}
                style={{ marginLeft: 'auto', background: '#fbeedd', border: `1px solid ${LBL.accent}`, borderRadius: 2, color: LBL.accent, fontSize: 18, fontWeight: 700, padding: '2px 8px', cursor: 'pointer', fontFamily: 'inherit' }}>
                Fyll alla
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Miniatyr med nypzoom ─────────────────────────────────────────────────────
// Innehållet har en fast naturlig storlek (px) och skalas till rutans bredd.
// Två fingrar zoomar runt mittpunkten, ett finger panorerar när man har zoomat in;
// ouppzoomad scrollar sidan som vanligt. På datorn: ctrl/nyp på styrplattan och knapparna.
function PinchZoom({ width, height, resetKey, children }: { width: number; height: number; resetKey: string; children: React.ReactNode }) {
  const { mode } = useThemeMode(); const T = getTheme(mode);
  const boxRef = useRef<HTMLDivElement>(null);
  const [boxW, setBoxW] = useState(0);
  const [view, setView] = useState({ zoom: 1, x: 0, y: 0 });
  const viewRef = useRef(view);
  viewRef.current = view;
  const moved = useRef(false);

  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const measure = () => setBoxW(el.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => { setView({ zoom: 1, x: 0, y: 0 }); }, [resetKey]);

  const fit = boxW > 0 ? boxW / width : 0;
  const boxH = Math.round(height * fit);
  const dims = useRef({ w: 0, h: 0 });
  dims.current = { w: boxW, h: boxH };

  const apply = useCallback((next: { zoom: number; x: number; y: number }) => {
    const p = clampPan(next.x, next.y, next.zoom, dims.current.w, dims.current.h);
    setView({ zoom: next.zoom, x: p.x, y: p.y });
  }, []);

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    let pinch: { dist: number; mid: { x: number; y: number }; start: typeof view } | null = null;
    let pan: { x: number; y: number; start: typeof view } | null = null;
    const local = (t: { clientX: number; clientY: number }) => {
      const r = el.getBoundingClientRect();
      return { x: t.clientX - r.left, y: t.clientY - r.top };
    };
    const two = (e: TouchEvent) => {
      const a = local(e.touches[0]); const b = local(e.touches[1]);
      return { dist: Math.hypot(a.x - b.x, a.y - b.y) || 1, mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } };
    };
    const onStart = (e: TouchEvent) => {
      if (e.touches.length === 1) moved.current = false;
      if (e.touches.length >= 2) {
        const g = two(e);
        pinch = { ...g, start: viewRef.current };
        pan = null;
        e.preventDefault();
      } else if (e.touches.length === 1 && viewRef.current.zoom > 1) {
        pan = { ...local(e.touches[0]), start: viewRef.current };
      }
    };
    const onMove = (e: TouchEvent) => {
      if (pinch && e.touches.length >= 2) {
        e.preventDefault();
        moved.current = true;
        const g = two(e);
        const z = zoomAround(pinch.start, pinch.start.zoom * (g.dist / pinch.dist), pinch.mid.x, pinch.mid.y);
        apply({ zoom: z.zoom, x: z.x + (g.mid.x - pinch.mid.x), y: z.y + (g.mid.y - pinch.mid.y) });
      } else if (pan && e.touches.length === 1) {
        const p = local(e.touches[0]);
        const dx = p.x - pan.x; const dy = p.y - pan.y;
        if (!moved.current && Math.hypot(dx, dy) < 4) return;
        e.preventDefault();
        moved.current = true;
        apply({ zoom: pan.start.zoom, x: pan.start.x + dx, y: pan.start.y + dy });
      }
    };
    const onEnd = (e: TouchEvent) => {
      if (e.touches.length < 2) pinch = null;
      if (e.touches.length === 1 && viewRef.current.zoom > 1) pan = { ...local(e.touches[0]), start: viewRef.current };
      if (e.touches.length === 0) pan = null;
    };
    // Nyp på styrplattan kommer som ctrl + hjul
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      const p = local(e);
      const d = Math.max(-40, Math.min(40, e.deltaY));
      apply(zoomAround(viewRef.current, viewRef.current.zoom * Math.exp(-d * 0.01), p.x, p.y));
    };
    // Safaris egen sidzoom ska inte ta över nypet
    const stopGesture = (e: Event) => e.preventDefault();
    el.addEventListener('touchstart', onStart, { passive: false });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('gesturestart', stopGesture);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('gesturestart', stopGesture);
    };
  }, [apply]);

  // Musdrag när man har zoomat in (datorn)
  const mouse = useRef<{ x: number; y: number; start: typeof view } | null>(null);
  function onMouseDown(e: React.MouseEvent) {
    if (view.zoom <= 1 || e.button !== 0) return;
    moved.current = false;
    mouse.current = { x: e.clientX, y: e.clientY, start: view };
  }
  function onMouseMove(e: React.MouseEvent) {
    const m = mouse.current;
    if (!m) return;
    const dx = e.clientX - m.x; const dy = e.clientY - m.y;
    if (!moved.current && Math.hypot(dx, dy) < 4) return;
    moved.current = true;
    apply({ zoom: m.start.zoom, x: m.start.x + dx, y: m.start.y + dy });
  }
  function onMouseUp() { mouse.current = null; }

  const stepZoom = (factor: number) => apply(zoomAround(view, view.zoom * factor, boxW / 2, boxH / 2));
  const zbtn: React.CSSProperties = { width: 40, height: 40, borderRadius: 20, border: `1px solid ${T.border}`, background: T.surface, color: T.text, fontSize: 18, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' };

  return (
    <div>
      <div
        ref={boxRef}
        data-testid="label-miniature"
        onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
        onClickCapture={e => { if (moved.current) { e.stopPropagation(); e.preventDefault(); moved.current = false; } }}
        style={{
          position: 'relative', width: '100%', height: boxH || 200, overflow: 'hidden',
          borderRadius: 6, background: mode === 'dark' ? '#1d2318' : '#d9d6cc',
          touchAction: view.zoom > 1 ? 'none' : 'pan-x pan-y',
          cursor: view.zoom > 1 ? (mouse.current ? 'grabbing' : 'grab') : 'default',
          boxShadow: '0 2px 10px rgba(0,0,0,0.25)',
        }}>
        {fit > 0 && (
          <div style={{ position: 'absolute', left: 0, top: 0, width, height, transformOrigin: '0 0', transform: `translate(${view.x}px, ${view.y}px) scale(${fit * view.zoom})` }}>
            {children}
          </div>
        )}
      </div>
      <div className="no-print" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 6, marginTop: 8 }}>
        <span style={{ color: T.textMuted, fontSize: 12, marginRight: 'auto' }}>{view.zoom > 1 ? `${Math.round(view.zoom * 100)} %` : 'Nyp eller tryck + för att zooma'}</span>
        {view.zoom > 1 && <button type="button" style={{ ...zbtn, width: 'auto', padding: '0 12px', fontSize: 13 }} onClick={() => setView({ zoom: 1, x: 0, y: 0 })}>Hela</button>}
        <button type="button" aria-label="Zooma ut" style={zbtn} onClick={() => stepZoom(1 / 1.5)}>−</button>
        <button type="button" aria-label="Zooma in" style={zbtn} onClick={() => stepZoom(1.5)}>+</button>
      </div>
    </div>
  );
}

// ─── Prisfält: uträknat (låst) när kilopris och vikt finns, annars eget pris ───
function PriceInput({ id, label, weightByHand, field, fieldLabel, withLabel, ariaLabel, onChange }: {
  id: string; label: LabelData; weightByHand: boolean; field: React.CSSProperties;
  fieldLabel?: React.CSSProperties; withLabel?: boolean; ariaLabel?: string; onChange: (v: string) => void;
}) {
  const pricing = labelPricing(label, weightByHand);
  const input = (
    <input id={id} aria-label={ariaLabel} type="text" inputMode="decimal"
      value={pricing.priceComputed ? pricing.price.text : label.totalPrice}
      readOnly={pricing.priceComputed}
      placeholder={pricing.price.mode === 'blank' ? 'för hand' : 'pris'}
      onChange={e => onChange(e.target.value)}
      style={{ ...field, ...(pricing.priceComputed ? { opacity: 0.75 } : null) }} />
  );
  if (!withLabel) return input;
  return (
    <>
      <label style={fieldLabel} htmlFor={id}>Pris (kr){pricing.priceComputed ? ' — kilopris × vikt' : ''}</label>
      {input}
    </>
  );
}

// ─── Plus/minus-väljare ───────────────────────────────────────────────────────
function Stepper({ value, onChange, label, min = 0, max = 99 }: { value: number; onChange: (n: number) => void; label: string; min?: number; max?: number }) {
  const { mode } = useThemeMode(); const T = getTheme(mode);
  const set = (n: number) => onChange(Math.max(min, Math.min(max, Math.round(Number.isFinite(n) ? n : min))));
  const btn: React.CSSProperties = { width: 40, height: 40, borderRadius: 20, border: `1px solid ${T.border}`, background: T.surfaceHover, color: T.text, fontSize: 20, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', lineHeight: 1, flexShrink: 0 };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <button type="button" aria-label={`Färre ${label}`} style={{ ...btn, opacity: value <= min ? 0.4 : 1 }} disabled={value <= min} onClick={() => set(value - 1)}>−</button>
      <input type="number" inputMode="numeric" aria-label={`Antal ${label}`} min={min} max={max} value={value}
        onChange={e => set(Number(e.target.value))}
        style={{ width: 48, height: 40, textAlign: 'center', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, color: T.text, fontSize: 17, fontWeight: 700, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }} />
      <button type="button" aria-label={`Fler ${label}`} style={{ ...btn, opacity: value >= max ? 0.4 : 1 }} disabled={value >= max} onClick={() => set(value + 1)}>+</button>
    </div>
  );
}

// ─── Step indicator ───────────────────────────────────────────────────────────
function StepIndicator({ steps, step }: { steps: { key: Step; label: string }[]; step: Step }) {
  const { mode } = useThemeMode(); const T = getTheme(mode);
  const idx = steps.findIndex(s => s.key === step);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 3, marginBottom: 20, flexWrap: 'wrap' }}>
      {steps.map((s, i) => (
        <React.Fragment key={s.key}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '4px 8px', borderRadius: 10, whiteSpace: 'nowrap',
            background: i === idx ? T.amberDim : 'transparent',
            border: `1px solid ${i === idx ? T.amber : i < idx ? T.forest : T.border}`,
            color: i === idx ? T.amber : i < idx ? T.success : T.textMuted,
            fontSize: 12, fontWeight: i === idx ? 700 : 500,
          }}>
            <div style={{
              width: 18, height: 18, borderRadius: '50%',
              background: i === idx ? T.amber : i < idx ? T.success : T.border,
              color: i <= idx ? '#000' : T.textMuted,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, fontWeight: 700, flexShrink: 0,
            }}>
              {i < idx ? '✓' : i + 1}
            </div>
            {s.label}
          </div>
          {i < steps.length - 1 && <div style={{ width: 6, height: 2, background: i < idx ? T.success : T.border }} />}
        </React.Fragment>
      ))}
    </div>
  );
}

const VILT_STEPS: { key: Step; label: string }[] = [
  { key: 'amounts', label: 'Färs & gryt' },
  { key: 'cuts',    label: 'Styckdetaljer' },
  { key: 'preview', label: 'Skriv ut' },
];
const EQUIP_STEPS: { key: Step; label: string }[] = [
  { key: 'select',  label: 'Välj typ' },
  { key: 'edit',    label: 'Redigera' },
  { key: 'preview', label: 'Skriv ut' },
];

// ─── Sparade utskriftsval ─────────────────────────────────────────────────────
const PREFS_KEY = 'qvenari_label_prefs';
interface PrintPrefs { output: PrintOutput; sheet: SheetFormat; printerSize: string; weightMode: 'hand' | 'enter' }
const DEFAULT_PREFS: PrintPrefs = { output: 'sheet', sheet: DEFAULT_SHEET, printerSize: PRINTER_SIZES[0].id, weightMode: 'hand' };

function loadPrefs(): PrintPrefs {
  try {
    const v = JSON.parse(localStorage.getItem(PREFS_KEY) || '{}');
    return {
      output: v.output === 'printer' ? 'printer' : 'sheet',
      sheet: v.sheet && v.sheet.cols >= 1 && v.sheet.cols <= 4 && v.sheet.rows >= 1 && v.sheet.rows <= 10 ? { cols: v.sheet.cols, rows: v.sheet.rows } : DEFAULT_SHEET,
      printerSize: PRINTER_SIZES.some(p => p.id === v.printerSize) ? v.printerSize : DEFAULT_PREFS.printerSize,
      weightMode: v.weightMode === 'enter' ? 'enter' : 'hand',
    };
  } catch { return DEFAULT_PREFS; }
}

function savePrefs(p: PrintPrefs) {
  try { localStorage.setItem(PREFS_KEY, JSON.stringify(p)); } catch { /* ignore */ }
}

function todayIso(): string { return new Date().toLocaleDateString('sv-SE'); }

/** Reglerna för utskriftskopian i @media print (webbläsaren; apparna skriver ut en PDF, se handlePrint). */
function printCopyCss(prefix: string, pageSize: string): string {
  return `
    ${prefix ? prefix.trim() : 'html'}, ${prefix}body {
      margin: 0 !important; padding: 0 !important;
      background: #ffffff !important;
    }
    ${prefix}body > *:not(#label-print-root) { display: none !important; }
    ${prefix}#label-print-root { display: block !important; }
    ${prefix}#label-print-root * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    ${prefix}.label-page {
      ${pageSize}
      background: #ffffff !important;
      overflow: hidden;
      break-after: page;
      page-break-after: always;
    }
    ${prefix}.label-page:last-child { break-after: auto; page-break-after: auto; }
    ${prefix}.label-card {
      width: 100% !important;
      height: 100% !important;
      background: #ffffff !important;
      border: none !important;
      page-break-inside: avoid;
      box-sizing: border-box !important;
    }
    ${prefix}.no-print { display: none !important; }`;
}

// ─── Main LabelPrinter component ──────────────────────────────────────────────
export function LabelPrinter({ user: _user, isAdmin: _isAdmin, defaultMarkName, embedded, onClose, preloadedLabels, initialTemplateType }: LabelPrinterProps) {
  const { mode } = useThemeMode(); const T = getTheme(mode);
  const initialType: TemplateType = initialTemplateType ?? 'vilt';
  const [templateType, setTemplateType] = useState<TemplateType>(initialType);
  const isVilt = templateType === 'vilt';
  const [step, setStep] = useState<Step>(initialType === 'vilt' ? 'amounts' : preloadedLabels ? 'edit' : 'select');

  // Vilt: ett registrerat djur (Köttdelning) ger art, mark, QR och anteckning
  const base = initialType === 'vilt' ? preloadedLabels?.[0] : undefined;
  // ET6: varje etikett får en QR-länk till sig själv. Djurets id (som tidigare var hela
  // QR-koden, ren text som inte ledde någonstans) följer med i anteckningen.
  const animalId = base && base.qrValue && !/^https?:/i.test(base.qrValue) && !base.qrValue.includes(base.id) ? base.qrValue : '';
  const baseNotes = publicNotes([animalId ? `Djur ${animalId}` : '', base?.notes ?? ''].filter(Boolean).join(' • '));
  const [species, setSpecies]       = useState<string>(base?.species || 'Rådjur');
  const [markName, setMarkName]     = useState<string>(base?.markName ?? defaultMarkName ?? '');
  const [styckatDate, setStyckatDate] = useState<string>(base?.styckatDate || todayIso());
  const [kgPrice, setKgPrice]       = useState<string>('');
  const [mince, setMince] = useState(5);
  const [stew, setStew]   = useState(5);
  const [customCuts, setCustomCuts] = useState<string[]>(loadCustomCuts);
  const [cutCounts, setCutCounts] = useState<Record<string, number>>(() => ({ ...DEFAULT_CUT_COUNTS }));
  const [newCut, setNewCut] = useState('');
  const cutNames = [...CUT_LIST, ...customCuts.filter(c => !CUT_LIST.includes(c) && c !== MINCE && c !== STEW)];
  const cutOptions = [...STYCKDETALJER, ...customCuts.filter(c => !STYCKDETALJER.includes(c))];

  // Utrustning (Profil): rutnät och etiketter som tidigare
  const [selectedCategory, setSelectedCategory] = useState<string>('Gevär');
  const [rows, setRows] = useState(preloadedLabels ? Math.min(5, Math.ceil(preloadedLabels.length / 2)) : 5);
  const [cols, setCols] = useState(2);

  const [labels, setLabels]         = useState<LabelData[]>(initialType === 'vilt' ? [] : (preloadedLabels ?? []).map(l => ({ ...l, qrValue: labelQrUrl(l.id) })));
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [prefs, setPrefsState]      = useState<PrintPrefs>(loadPrefs);
  const [pageIdx, setPageIdx]       = useState(0);
  const [printOnly, setPrintOnly]   = useState<number | null>(null);
  const [printError, setPrintError] = useState<string | null>(null);
  const [pdfBusy, setPdfBusy]       = useState(false);

  const setPrefs = useCallback((patch: Partial<PrintPrefs>) => {
    setPrefsState(prev => { const next = { ...prev, ...patch }; savePrefs(next); return next; });
    setPageIdx(0);
  }, []);

  const weightByHand = isVilt && prefs.weightMode === 'hand';
  const printerSize = PRINTER_SIZES.find(p => p.id === prefs.printerSize) ?? PRINTER_SIZES[0];
  // Utrustning använder sitt eget rutnät, vilt arkformatet från förhandsvisningen
  const sheet: SheetFormat = isVilt ? prefs.sheet : { cols, rows };

  const handleAddCustomCut = useCallback((cut: string) => {
    const v = cut.trim();
    if (!v) return;
    setCustomCuts(prev => {
      if (prev.includes(v) || STYCKDETALJER.includes(v)) return prev;
      const next = [...prev, v];
      saveCustomCuts(next);
      return next;
    });
  }, []);

  // Gemensamma fält ändras på alla etiketter som redan finns
  const setShared = useCallback((patch: Partial<LabelData>) => {
    setLabels(prev => prev.map(l => ({ ...l, ...patch })));
  }, []);

  const makeViltLabel = useCallback((cut: string): LabelData => {
    const fresh = generateLabels(1, 1, species, cut, markName)[0];
    return {
      ...fresh,
      styckatDate, kgPrice,
      skjutetDate: base?.skjutetDate ?? '',
      notes: baseNotes,
      viltRegId: base?.viltRegId,
    };
  }, [species, markName, styckatDate, kgPrice, base?.skjutetDate, baseNotes, base?.viltRegId]);

  const goToPreview = useCallback(() => {
    const seq = cutSequence(cutNames.map(cut => ({ cut, count: cutCounts[cut] ?? 0 })), stew, mince);
    setLabels(prev => reconcileLabels(prev, seq, makeViltLabel));
    setEditingIdx(null);
    setPageIdx(0);
    setStep('preview');
  }, [cutNames, cutCounts, stew, mince, makeViltLabel]);

  const handleProceedToEdit = useCallback(() => {
    setLabels(generateLabels(rows, cols, selectedCategory, 'Namn på utrustning', defaultMarkName || ''));
    setEditingIdx(null);
    setStep('edit');
  }, [rows, cols, selectedCategory, defaultMarkName]);

  const handleLabelChange = useCallback((idx: number, updated: LabelData) => {
    setLabels(prev => prev.map((l, i) => i === idx ? updated : l));
  }, []);

  const handleFillAll = useCallback((idx: number) => {
    setLabels(prev => {
      const src = prev[idx];
      return prev.map((l, i) => i === idx ? l : {
        ...l,
        species: src.species, cut: src.cut, markName: src.markName,
        skjutetDate: src.skjutetDate, styckatDate: src.styckatDate,
        weightKg: src.weightKg, kgPrice: src.kgPrice,
        autoPrice: src.autoPrice, totalPrice: src.totalPrice, notes: src.notes,
      });
    });
  }, []);

  const setCopies = useCallback((idx: number, copies: number) => {
    const n = Math.max(1, Math.min(99, Math.round(copies) || 1));
    setLabels(prev => prev.map((l, i) => i === idx ? { ...l, copies: n } : l));
  }, []);

  const removeLabel = useCallback((idx: number) => {
    // Vilt: antalet i steg 1–2 följer med, så etiketten inte kommer tillbaka
    const cut = isVilt ? labels[idx]?.cut : undefined;
    if (cut === MINCE) setMince(n => Math.max(0, n - 1));
    else if (cut === STEW) setStew(n => Math.max(0, n - 1));
    else if (cut) setCutCounts(prev => ({ ...prev, [cut]: Math.max(0, (prev[cut] ?? 0) - 1) }));
    setLabels(prev => prev.filter((_, i) => i !== idx));
    setEditingIdx(null);
  }, [isVilt, labels]);

  const addLabel = useCallback(() => {
    setLabels(prev => {
      const last = prev[prev.length - 1];
      const fresh = generateLabels(1, 1, last?.species ?? selectedCategory, 'Namn på utrustning', last?.markName ?? defaultMarkName ?? '')[0];
      return last ? [...prev, { ...fresh, notes: last.notes }] : [fresh];
    });
    setEditingIdx(labels.length);
  }, [labels.length, selectedCategory, defaultMarkName]);

  // Save labels to API so QR codes resolve to real data
  // ET6: etiketterna sparas när de skapas och ändras (inte bara vid "Skriv ut"), så att varje
  // QR-kod som syns – även i en PDF, skärmbild eller webbläsarens egen utskrift – leder till dem.
  const savedRef = useRef(new Map<string, string>());
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const toPayload = useCallback((l: LabelData, type: TemplateType, byHand: boolean) => ({
    id: l.id,
    species: l.species,
    cut: l.cut,
    markName: l.markName,
    skjutetDate: l.skjutetDate,
    styckatDate: l.styckatDate,
    weightKg: byHand ? '' : l.weightKg,
    kgPrice: l.kgPrice,
    totalPrice: labelTotal(l, byHand),
    notes: l.notes,
    templateType: type,
    ...(l.viltRegId ? { viltRegId: l.viltRegId } : {}),
  }), []);
  const saveLabelsToApi = useCallback(async (lbls: LabelData[], type: TemplateType, byHand: boolean): Promise<boolean> => {
    const changed = changedSince(lbls.map(l => toPayload(l, type, byHand)), savedRef.current);
    if (!changed.length) { setSaveState('saved'); return true; }
    setSaveState('saving');
    try {
      const res = await fetch(resolveUrl('/api/labels'), {
        method: 'POST',
        // Inloggningen behövs för kopplingen till djuret (bara den som har tillgång till marken)
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ labels: changed }),
      });
      if (!res.ok) throw new Error(String(res.status));
      for (const c of changed) savedRef.current.set(c.id, JSON.stringify(c));
      setSaveState('saved');
      return true;
    } catch {
      setSaveState('error');
      return false;
    }
  }, [toPayload]);

  // Spara (fördröjt) så fort etiketterna visas eller ändras i utskriftssteget
  const saveNowRef = useRef<() => Promise<boolean>>(async () => true);
  saveNowRef.current = () => saveLabelsToApi(labels, templateType, weightByHand);
  useEffect(() => {
    if (step !== 'preview' || !labels.length) return;
    const t = setTimeout(() => { void saveNowRef.current(); }, 600);
    return () => clearTimeout(t);
  }, [step, labels, templateType, weightByHand]);
  // Webbläsarens egen utskrift (Cmd/Ctrl+P) går förbi knappen
  useEffect(() => {
    if (step !== 'preview') return;
    const onBefore = () => { void saveNowRef.current(); };
    window.addEventListener('beforeprint', onBefore);
    return () => window.removeEventListener('beforeprint', onBefore);
  }, [step]);

  // Varje etikett upprepas enligt sitt antal ex
  const printItems = labels.flatMap((l, idx) =>
    Array.from({ length: Math.max(1, l.copies ?? 1) }, (_, k) => ({ label: l, idx, key: `${l.id}-${k}` })),
  );
  const perPage = Math.max(1, sheet.rows * sheet.cols);
  const sheetPages = chunk(printItems, perPage);
  const isPrinter = prefs.output === 'printer';
  const pageCount = isPrinter ? printItems.length : sheetPages.length;
  const curPage = Math.min(pageIdx, Math.max(0, pageCount - 1));

  /**
   * Skriv ut eller visa som PDF (ET8). Apparna och "Visa som PDF" använder den egenritade PDF:en med
   * fasta mått (ET4); webbläsarens utskrift använder @media print som förut.
   */
  const handleOutput = useCallback(async (mode: 'print' | 'pdf', only: number | null) => {
    const native = isNativeApp();
    // Webbläsaren: fliken för PDF:en måste öppnas direkt i klicket, annars stoppas den som popup
    const tab = mode === 'pdf' && !native ? window.open('', '_blank') : null;
    if (mode === 'print') setPrintOnly(only);
    // Alla etiketter sparas före utskriften; misslyckas det skrivs ändå ut, med en varning
    await saveLabelsToApi(labels, templateType, weightByHand);
    setPrintError(null);
    const items = (only != null && printItems[only] ? [printItems[only]] : printItems).map(it => it.label);
    const pages = isPrinter ? printerPdfPages(items, printerSize) : sheetPdfPages(items, sheet);
    const page = isPrinter ? { pageWidthMm: printerSize.wMm, pageHeightMm: printerSize.hMm } : { pageWidthMm: 210, pageHeightMm: 297 };
    const jobName = isVilt ? 'Köttetiketter' : 'Etiketter';
    // Rader i API-loggen (sök labelPrint): vilken väg utskriften tog och hur långt den kom
    const started = Date.now();
    const log: Record<string, unknown> = {
      id: Math.random().toString(36).slice(2, 10), buildId: APP_BUILD_ID,
      platform: nativePlatform(), native, plugin: nativePrintAvailable(),
      output: prefs.output, cols: sheet.cols, rows: sheet.rows, template: templateType,
      labels: items.length, pages: pages.length, perPage: pages.map(p => p.cells.length), ...page,
    };
    const sendLog = (stage: string, extra: Record<string, unknown> = {}) => {
      Object.assign(log, extra, { stage, ms: Date.now() - started });
      fetch(resolveUrl('/api/labels/print-log'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(log), keepalive: true,
      }).catch(() => {});
    };
    sendLog(mode === 'pdf' ? 'pdf' : 'click');
    if (mode === 'print' && !native) {
      // Låt utskriftskopian renderas (och QR-koderna ritas) innan dialogen öppnas
      setTimeout(() => window.print(), 250);
      return;
    }
    setPdfBusy(true);
    try {
      const pdf = await renderLabelPdf(pages, { template: isVilt ? 'vilt' : 'equip', weightByHand, equipEmojis: EQUIP_EMOJIS });
      if (mode === 'pdf') {
        await showPdf(pdf, { fileName: `${jobName}.pdf`, tab });
        sendLog('pdf-shown', { pdfBytes: pdf.length });
      } else {
        sendLog('dialog', { pdfBytes: pdf.length });
        setPdfBusy(false);
        const res = await printPdf(pdf, { jobName, ...page });
        sendLog('done', { result: res.completed ? 'printed' : 'closed', paperWidthMm: res.paperWidthMm, paperHeightMm: res.paperHeightMm });
      }
    } catch (e) {
      tab?.close();
      const msg = e instanceof Error ? e.message : (mode === 'pdf' ? 'PDF:en gick inte att visa.' : 'Utskriften gick inte att starta.');
      setPrintError(msg);
      sendLog('error', { result: 'error', error: msg });
    } finally {
      setPdfBusy(false);
    }
  }, [labels, printItems, sheet, prefs.output, templateType, weightByHand, saveLabelsToApi, isPrinter, printerSize, isVilt]);
  const handlePrint = useCallback((only: number | null) => handleOutput('print', only), [handleOutput]);

  useEffect(() => {
    const done = () => setPrintOnly(null);
    window.addEventListener('afterprint', done);
    return () => window.removeEventListener('afterprint', done);
  }, []);

  const renderCard = (label: LabelData, key: string, idx?: number) => isVilt ? (
    <LabelCard key={key} label={label} editing={false} onChange={() => {}} printMode weightByHand={weightByHand}
      onClick={idx != null ? () => setEditingIdx(idx) : undefined} />
  ) : (
    <EquipLabelCard key={key} label={label} editing={false} onChange={() => {}} printMode={true} />
  );

  const renderSheet = (page: typeof printItems, key: React.Key, interactive: boolean) => (
    <div key={key} className="label-page"
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${sheet.cols}, 1fr)`,
        // Etikettarket: 55 mm höga etiketter fördelade jämnt mellan 5 mm marginal uppe och nere
        gridTemplateRows: sheet.rows * LABEL_H_MM <= PAGE_H_MM ? `repeat(${sheet.rows}, ${LABEL_H_MM}mm)` : `repeat(${sheet.rows}, 1fr)`,
        alignContent: 'space-between',
        columnGap: '2mm',
        rowGap: sheet.rows * LABEL_H_MM <= PAGE_H_MM ? 0 : '2mm',
        width: '196mm',
        height: `${PAGE_H_MM}mm`,
        boxSizing: 'border-box',
        background: '#ffffff',
      }}>
      {page.map(({ label, key: k, idx }) => renderCard(label, k, interactive ? idx : undefined))}
    </div>
  );

  const renderSingle = (item: (typeof printItems)[number], key: React.Key, interactive: boolean) => (
    <div key={key} className="label-page" style={{ width: `${printerSize.wMm}mm`, height: `${printerSize.hMm}mm`, boxSizing: 'border-box', background: '#ffffff' }}>
      {renderCard(item.label, item.key, interactive ? item.idx : undefined)}
    </div>
  );

  const renderPrintRoot = () => {
    if (isPrinter) {
      const items = printOnly != null && printItems[printOnly] ? [printItems[printOnly]] : printItems;
      return items.map((it, i) => renderSingle(it, i, false));
    }
    return sheetPages.map((page, pi) => renderSheet(page, pi, false));
  };

  // Miniatyren: ett A4-ark (210 × 297 mm) eller en etikett från etikettskrivaren
  const miniW = isPrinter ? printerSize.wMm * MM_TO_PX : 210 * MM_TO_PX;
  const miniH = isPrinter ? printerSize.hMm * MM_TO_PX : 297 * MM_TO_PX;
  const miniature = pageCount === 0 ? null : isPrinter ? (
    <div style={{ width: miniW, height: miniH, background: '#fff' }}>
      {renderSingle(printItems[curPage], 'mini', isVilt)}
    </div>
  ) : (
    <div style={{ width: miniW, height: miniH, background: '#fff', padding: '5mm 7mm', boxSizing: 'border-box' }}>
      {renderSheet(sheetPages[curPage], 'mini', isVilt)}
    </div>
  );

  const sectionTitle: React.CSSProperties = { color: T.text, fontSize: 20, fontWeight: 800, marginBottom: 4, letterSpacing: '-0.01em' };
  const sectionSub:   React.CSSProperties = { color: T.textSub, fontSize: 14, marginBottom: 18, lineHeight: 1.45 };
  const card: React.CSSProperties         = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: 16, boxSizing: 'border-box' };
  const capsLabel: React.CSSProperties    = { color: T.textSub, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 };
  const field: React.CSSProperties        = { background: T.surfaceHover, border: `1px solid ${T.border}`, borderRadius: 8, color: T.text, fontSize: 15, padding: '9px 11px', fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box', colorScheme: mode === 'dark' ? 'dark' : 'light' };
  const fieldLabel: React.CSSProperties   = { display: 'block', color: T.textSub, fontSize: 12, marginBottom: 4, fontWeight: 600 };

  const btnPrimary: React.CSSProperties = {
    background: T.amber, color: '#000', border: 'none', borderRadius: 8,
    padding: '12px 22px', fontSize: 15, fontWeight: 700, cursor: 'pointer',
    fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  };
  const btnSecondary: React.CSSProperties = {
    background: T.surface, color: T.text, border: `1px solid ${T.border}`, borderRadius: 8,
    padding: '12px 18px', fontSize: 15, cursor: 'pointer',
    fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  };
  const stepBtn: React.CSSProperties = {
    width: 28, height: 28, background: T.surfaceHover, border: `1px solid ${T.border}`, borderRadius: 4,
    color: T.text, fontSize: 15, cursor: 'pointer', fontFamily: 'inherit', lineHeight: 1,
  };
  const chip = (active: boolean): React.CSSProperties => ({
    padding: '8px 14px', minHeight: 38, background: active ? T.amberDim : T.surfaceHover,
    border: `1px solid ${active ? T.amber : T.border}`, borderRadius: 999,
    color: active ? T.amber : T.text, fontSize: 14, fontWeight: active ? 700 : 500,
    cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6,
  });
  const segment = (active: boolean): React.CSSProperties => ({
    flex: 1, padding: '10px 8px', minHeight: 44, border: 'none', borderRadius: 8,
    background: active ? T.amber : 'transparent', color: active ? '#000' : T.text,
    fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
  });
  const segWrap: React.CSSProperties = { display: 'flex', gap: 4, padding: 4, background: T.surfaceHover, border: `1px solid ${T.border}`, borderRadius: 10 };
  const navRow: React.CSSProperties  = { display: 'flex', gap: 10, marginTop: 20 };

  const totalCuts = cutNames.reduce((s, c) => s + (cutCounts[c] ?? 0), 0);
  const totalLabels = totalCuts + stew + mince;
  const editing = editingIdx != null && isVilt && step === 'preview' ? labels[editingIdx] : undefined;

  return (
    <div style={{ padding: embedded ? '8px 0' : '24px 16px', maxWidth: isVilt || step === 'preview' ? 560 : 1100, margin: '0 auto', paddingTop: embedded ? 8 : 80, boxSizing: 'border-box' }}>
      <style>{`
        @page { margin: ${isPrinter ? '0' : `${SHEET_MARGIN_MM.top}mm ${SHEET_MARGIN_MM.side}mm`}; size: ${isPrinter ? `${printerSize.wMm}mm ${printerSize.hMm}mm` : 'A4 portrait'}; }
        #label-print-root { display: none; }
        @media print {
          ${printCopyCss('', isPrinter ? `width: ${printerSize.wMm}mm !important; height: ${printerSize.hMm}mm !important;` : `width: 100% !important; height: ${PAGE_H_MM}mm !important;`)}
        }
      `}</style>

      {/* Header */}
      <div style={{ marginBottom: 16 }} className="no-print">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 8, background: T.amberDim, border: `1px solid ${T.amber}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>🏷️</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ color: T.text, fontSize: embedded ? 20 : 24, fontWeight: 800, margin: 0 }}>
              {isVilt ? 'Köttetiketter' : 'Etiketter'}{defaultMarkName ? ` — ${defaultMarkName}` : ''}
            </h1>
            <div style={{ color: T.textSub, fontSize: 13 }}>{isVilt ? 'Etiketter för vakuumförpackat viltkött' : 'Etiketter för utrustning'}</div>
          </div>
          {embedded && onClose && (
            <button onClick={onClose} style={{ background: T.surfaceHover, border: `1px solid ${T.border}`, borderRadius: 8, color: T.text, fontSize: 14, fontWeight: 600, padding: '8px 14px', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
              Stäng
            </button>
          )}
        </div>
      </div>

      <div className="no-print"><StepIndicator steps={isVilt ? VILT_STEPS : EQUIP_STEPS} step={step} /></div>

      {/* ─── VILT 1: FÄRS OCH GRYTBITAR ─────────────────────────────────── */}
      {isVilt && step === 'amounts' && (
        <div className="no-print">
          <div style={sectionTitle}>Färs och grytbitar</div>
          <div style={sectionSub}>Hur många paket färs och grytbitar blev det? Styckdetaljerna väljer du i nästa steg.</div>

          <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 14 }}>
            {([[MINCE, mince, setMince], [STEW, stew, setStew]] as const).map(([name, val, set]) => (
              <div key={name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div>
                  <div style={{ color: T.text, fontSize: 17, fontWeight: 700 }}>{name}</div>
                  <div style={{ color: T.textMuted, fontSize: 12 }}>{val} {val === 1 ? 'etikett' : 'etiketter'}</div>
                </div>
                <Stepper value={val} onChange={set} label={name.toLowerCase()} />
              </div>
            ))}
          </div>

          <div style={{ ...card, marginBottom: 14 }}>
            <div style={capsLabel}>Djurart</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {VILT_SPECIES.map(sp => (
                <button key={sp} type="button" onClick={() => { setSpecies(sp); setShared({ species: sp }); }} style={chip(species === sp)}>
                  <span>{SPECIES_EMOJIS[sp] || '🦌'}</span><span>{sp}</span>
                </button>
              ))}
            </div>
          </div>

          <div style={{ ...card, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={fieldLabel} htmlFor="lbl-mark">Jaktmark</label>
              <input id="lbl-mark" type="text" value={markName} placeholder="Markens namn" onChange={e => { setMarkName(e.target.value); setShared({ markName: e.target.value }); }} style={field} />
            </div>
            <div>
              <label style={fieldLabel} htmlFor="lbl-styckat">Styckningsdag</label>
              <input id="lbl-styckat" type="date" value={styckatDate} onChange={e => { setStyckatDate(e.target.value); setShared({ styckatDate: e.target.value }); }} style={field} />
            </div>
            <div>
              <label style={fieldLabel} htmlFor="lbl-kgpris">Kilopris (valfritt)</label>
              <input id="lbl-kgpris" type="text" inputMode="decimal" value={kgPrice} placeholder="kr/kg" onChange={e => { setKgPrice(e.target.value); setShared({ kgPrice: e.target.value }); }} style={field} />
            </div>
          </div>

          <div style={navRow}>
            <button type="button" style={{ ...btnPrimary, flex: 1 }} onClick={() => setStep('cuts')}>Styckdetaljer →</button>
          </div>
        </div>
      )}

      {/* ─── VILT 2: STYCKDETALJER ───────────────────────────────────────── */}
      {isVilt && step === 'cuts' && (
        <div className="no-print">
          <div style={sectionTitle}>Styckdetaljer</div>
          <div style={sectionSub}>Ange antal paket av varje detalj. Totalt {totalLabels} etiketter ({totalCuts} styckdetaljer, {stew} grytbitar, {mince} färs).</div>

          <div style={{ ...card, padding: 0 }}>
            {cutNames.map((cut, i) => {
              const n = cutCounts[cut] ?? 0;
              return (
                <div key={cut} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 14px', borderTop: i ? `1px solid ${T.border}` : 'none' }}>
                  <div style={{ color: n > 0 ? T.text : T.textMuted, fontSize: 16, fontWeight: n > 0 ? 700 : 500, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{cut}</div>
                  <Stepper value={n} label={cut.toLowerCase()} onChange={v => setCutCounts(prev => ({ ...prev, [cut]: v }))} />
                </div>
              );
            })}
            <form onSubmit={e => { e.preventDefault(); const v = newCut.trim(); if (!v) return; handleAddCustomCut(v); setCutCounts(prev => ({ ...prev, [v]: Math.max(1, prev[v] ?? 0) })); setNewCut(''); }}
              style={{ display: 'flex', gap: 8, padding: '12px 14px', borderTop: `1px solid ${T.border}` }}>
              <input type="text" value={newCut} onChange={e => setNewCut(e.target.value)} placeholder="Egen styckdetalj" aria-label="Egen styckdetalj" style={{ ...field, flex: 1 }} />
              <button type="submit" style={{ ...btnSecondary, padding: '9px 14px' }} disabled={!newCut.trim()}>Lägg till</button>
            </form>
          </div>

          <div style={navRow}>
            <button type="button" style={btnSecondary} onClick={() => setStep('amounts')}>← Tillbaka</button>
            <button type="button" style={{ ...btnPrimary, flex: 1, opacity: totalLabels ? 1 : 0.5 }} disabled={!totalLabels} onClick={goToPreview}>Visa etiketter →</button>
          </div>
        </div>
      )}

      {/* ─── UTRUSTNING 1: VÄLJ KATEGORI ─────────────────────────────────── */}
      {!isVilt && step === 'select' && (
        <div className="no-print">
          <div style={sectionTitle}>Välj kategori</div>
          <div style={sectionSub}>Etiketter för utrustning med QR-kod</div>
          <div style={{ marginBottom: 20, ...card }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {UTRUSTNING_KATEGORIER.map(cat => (
                <button key={cat} type="button" onClick={() => setSelectedCategory(cat)} style={chip(selectedCategory === cat)}>
                  <span>{EQUIP_EMOJIS[cat] || '⚙️'}</span><span>{cat}</span>
                </button>
              ))}
            </div>
          </div>
          <div style={{ marginBottom: 20, ...card }}>
            <div style={capsLabel}>Antal etiketter — {rows * cols} st ({rows} rader &times; {cols} kolumner)</div>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              <div><div style={fieldLabel}>Rader</div><Stepper value={rows} onChange={setRows} label="rader" min={1} max={8} /></div>
              <div><div style={fieldLabel}>Kolumner</div><Stepper value={cols} onChange={setCols} label="kolumner" min={1} max={4} /></div>
            </div>
          </div>
          <button type="button" style={btnPrimary} onClick={handleProceedToEdit}>Redigera etiketter →</button>
        </div>
      )}

      {/* ─── UTRUSTNING 2: REDIGERA ──────────────────────────────────────── */}
      {!isVilt && step === 'edit' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 12 }} className="no-print">
            <div>
              <div style={sectionTitle}>Redigera etiketter</div>
              <div style={sectionSub}>Klicka på en etikett för att redigera. Ange antal ex under varje etikett — totalt {printItems.length} utskrifter.</div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" style={btnSecondary} onClick={() => setStep('select')}>← Tillbaka</button>
              <button type="button" style={btnPrimary} onClick={() => { setEditingIdx(null); setPageIdx(0); setStep('preview'); }}>Förhandsgranska →</button>
            </div>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
            {labels.map((label, idx) => {
              const isEditing = editingIdx === idx;
              return (
                <div key={label.id} style={{ position: 'relative' }}>
                  {isEditing && (
                    <button type="button" onClick={() => setEditingIdx(null)} className="no-print" aria-label="Klar"
                      style={{ position: 'absolute', top: -10, right: -10, zIndex: 10, width: 24, height: 24, borderRadius: '50%', background: T.success, border: 'none', color: '#000', fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'inherit', boxShadow: '0 2px 6px rgba(0,0,0,0.4)' }}>
                      ✓
                    </button>
                  )}
                  <EquipLabelCard label={label} editing={isEditing} onChange={u => handleLabelChange(idx, u)} onFillAll={() => handleFillAll(idx)} onClick={() => setEditingIdx(idx)} />
                  <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                    <span style={{ color: T.textSub, fontSize: 12 }}>Antal ex</span>
                    <button type="button" onClick={() => setCopies(idx, (label.copies ?? 1) - 1)} style={stepBtn}>−</button>
                    <input type="number" min={1} max={99} value={label.copies ?? 1} onChange={e => setCopies(idx, Number(e.target.value))}
                      style={{ width: 44, textAlign: 'center', background: T.surfaceHover, border: `1px solid ${T.border}`, borderRadius: 4, color: T.text, fontSize: 13, padding: '4px 2px', fontFamily: 'inherit', outline: 'none' }} />
                    <button type="button" onClick={() => setCopies(idx, (label.copies ?? 1) + 1)} style={stepBtn}>+</button>
                    {labels.length > 1 && (
                      <button type="button" onClick={() => removeLabel(idx)} title="Ta bort etikett"
                        style={{ marginLeft: 'auto', background: 'none', border: `1px solid ${T.border}`, borderRadius: 4, color: T.textMuted, fontSize: 12, padding: '4px 10px', cursor: 'pointer', fontFamily: 'inherit' }}>
                        Ta bort
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            <button type="button" onClick={addLabel} className="no-print"
              style={{ width: 340, height: 233, background: 'transparent', border: `2px dashed ${T.border}`, borderRadius: 4, color: T.textSub, fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
              + Lägg till etikett
            </button>
          </div>
        </div>
      )}

      {/* ─── SKRIV UT (båda typerna) ─────────────────────────────────────── */}
      {step === 'preview' && (
        <div>
          <div className="no-print">
            <div style={sectionTitle}>Skriv ut</div>
            <div style={sectionSub}>
              {printItems.length} etiketter{isPrinter ? ', en i taget på etikettskrivaren' : ` på ${sheetPages.length} ark (${sheet.cols} × ${sheet.rows})`}.
              {isVilt ? ' Tryck på en etikett för att ändra den.' : ''}
            </div>

            <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 14 }}>
              <div>
                <div style={capsLabel}>Skriv ut på</div>
                <div style={segWrap} role="radiogroup" aria-label="Skriv ut på">
                  <button type="button" role="radio" aria-checked={!isPrinter} style={segment(!isPrinter)} onClick={() => setPrefs({ output: 'sheet' })}>A4-ark</button>
                  <button type="button" role="radio" aria-checked={isPrinter} style={segment(isPrinter)} onClick={() => setPrefs({ output: 'printer' })}>Etikettskrivare</button>
                </div>
              </div>

              {!isPrinter && isVilt && (
                <div>
                  <div style={capsLabel}>Arkets format (kolumner × rader)</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                    {SHEET_PRESETS.map(f => (
                      <button key={`${f.cols}x${f.rows}`} type="button" style={chip(sheet.cols === f.cols && sheet.rows === f.rows)} onClick={() => setPrefs({ sheet: f })}>
                        {f.cols} × {f.rows}
                      </button>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 12 }}>
                    <div><div style={fieldLabel}>Kolumner</div><Stepper value={sheet.cols} min={1} max={4} label="kolumner" onChange={v => setPrefs({ sheet: { ...sheet, cols: v } })} /></div>
                    <div><div style={fieldLabel}>Rader</div><Stepper value={sheet.rows} min={1} max={10} label="rader" onChange={v => setPrefs({ sheet: { ...sheet, rows: v } })} /></div>
                  </div>
                </div>
              )}

              {isPrinter && (
                <div>
                  <label style={fieldLabel} htmlFor="lbl-printer-size">Etikettens storlek</label>
                  <select id="lbl-printer-size" value={printerSize.id} onChange={e => setPrefs({ printerSize: e.target.value })} style={field}>
                    {PRINTER_SIZES.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                  </select>
                  <div style={{ color: T.textMuted, fontSize: 12, marginTop: 6, lineHeight: 1.45 }}>
                    Varje etikett blir en egen sida i den storleken. Välj etikettskrivaren och samma pappersstorlek i utskriftsdialogen.
                  </div>
                </div>
              )}

              {isVilt && (
                <div>
                  <div style={capsLabel}>Vikt</div>
                  <div style={segWrap} role="radiogroup" aria-label="Vikt">
                    <button type="button" role="radio" aria-checked={weightByHand} style={segment(weightByHand)} onClick={() => setPrefs({ weightMode: 'hand' })}>Skriv för hand</button>
                    <button type="button" role="radio" aria-checked={!weightByHand} style={segment(!weightByHand)} onClick={() => setPrefs({ weightMode: 'enter' })}>Ange vikt</button>
                  </div>
                </div>
              )}
            </div>

            {isVilt && (
              <div style={{ ...card, padding: 0, marginBottom: 14, maxHeight: 320, overflowY: 'auto' }}>
                <div style={{ ...capsLabel, padding: '14px 14px 0', marginBottom: 6 }}>
                  {weightByHand ? 'Pris per etikett (kr)' : 'Vikt och pris per etikett'}
                  {!weightByHand && ` — ${labels.filter(l => l.weightKg.trim()).length} av ${labels.length} vikter`}
                </div>
                <div style={{ color: T.textMuted, fontSize: 12, padding: '0 14px 8px', lineHeight: 1.4 }}>
                  {labels.some(l => l.kgPrice.trim()) && !weightByHand ? 'Med kilopris räknas priset ut av vikten. ' : ''}Tomma fält skrivs inte ut{weightByHand ? '; vikten (och priset om kilopris finns) blir rutor att fylla i' : ''}.
                </div>
                {labels.map((l, i) => {
                  const nth = labels.slice(0, i + 1).filter(x => x.cut === l.cut).length;
                  const many = labels.filter(x => x.cut === l.cut).length > 1;
                  return (
                    <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderTop: `1px solid ${T.border}` }}>
                      <div style={{ flex: 1, color: T.text, fontSize: 15, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.cut}{many ? ` ${nth}` : ''}</div>
                      {!weightByHand && (
                        <>
                          <input id={`w-${l.id}`} aria-label={`Vikt, ${l.cut}${many ? ` ${nth}` : ''} (kg)`} type="text" inputMode="decimal" placeholder="vikt" value={l.weightKg}
                            onChange={e => handleLabelChange(i, { ...l, weightKg: e.target.value })}
                            style={{ ...field, width: 72, textAlign: 'right', padding: '9px 8px' }} />
                          <span style={{ color: T.textMuted, fontSize: 13 }}>kg</span>
                        </>
                      )}
                      <PriceInput id={`p-${l.id}`} label={l} weightByHand={weightByHand} field={{ ...field, width: 78, textAlign: 'right', padding: '9px 8px' }}
                        ariaLabel={`Pris, ${l.cut}${many ? ` ${nth}` : ''} (kr)`}
                        onChange={v => handleLabelChange(i, { ...l, totalPrice: v })} />
                      <span style={{ color: T.textMuted, fontSize: 13 }}>kr</span>
                    </div>
                  );
                })}
              </div>
            )}

            {pageCount > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <button type="button" style={{ ...btnSecondary, padding: '8px 14px', opacity: curPage > 0 ? 1 : 0.4 }} disabled={curPage === 0} onClick={() => setPageIdx(curPage - 1)} aria-label={isPrinter ? 'Föregående etikett' : 'Föregående ark'}>‹</button>
                <div style={{ color: T.textSub, fontSize: 14, fontWeight: 600 }}>{isPrinter ? 'Etikett' : 'Ark'} {curPage + 1} av {pageCount}</div>
                <button type="button" style={{ ...btnSecondary, padding: '8px 14px', opacity: curPage < pageCount - 1 ? 1 : 0.4 }} disabled={curPage >= pageCount - 1} onClick={() => setPageIdx(curPage + 1)} aria-label={isPrinter ? 'Nästa etikett' : 'Nästa ark'}>›</button>
              </div>
            )}
          </div>

          <div className="no-print">
            {miniature
              ? <PinchZoom width={miniW} height={miniH} resetKey={`${prefs.output}-${printerSize.id}-${sheet.cols}x${sheet.rows}-${curPage}`}>{miniature}</PinchZoom>
              : <div style={{ ...card, color: T.textSub }}>Inga etiketter att skriva ut.</div>}
          </div>

          <div className="no-print" style={{ ...navRow, flexWrap: 'wrap' }}>
            <button type="button" style={btnSecondary} onClick={() => { setEditingIdx(null); setStep(isVilt ? 'cuts' : 'edit'); }}>← {isVilt ? 'Styckdetaljer' : 'Redigera'}</button>
            {isPrinter && pageCount > 1 && (
              <button type="button" style={btnSecondary} onClick={() => handlePrint(curPage)}>Skriv ut denna</button>
            )}
            <button type="button" style={{ ...btnPrimary, flex: 1, background: T.success, color: mode === 'dark' ? '#000' : '#fff' }} disabled={!pageCount} onClick={() => handlePrint(null)}>
              🖨️ Skriv ut{isPrinter && pageCount > 1 ? ` alla (${pageCount})` : ''}
            </button>
            <button type="button" style={{ ...btnSecondary, opacity: pdfBusy ? 0.6 : 1 }} disabled={!pageCount || pdfBusy} onClick={() => handleOutput('pdf', null)}>
              {pdfBusy ? 'Skapar PDF…' : '📄 Visa som PDF'}
            </button>
          </div>

          <div className="no-print" role="status" aria-live="polite" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 13, color: saveState === 'error' ? T.danger : T.textMuted, lineHeight: 1.4 }}>
            {saveState === 'saving' && 'Sparar etiketterna…'}
            {saveState === 'saved' && '✓ Etiketterna är sparade – QR-koderna leder till dem.'}
            {saveState === 'error' && (
              <>
                <span style={{ flex: 1 }}>Etiketterna kunde inte sparas. QR-koderna fungerar inte förrän de är sparade.</span>
                <button type="button" style={{ ...btnSecondary, padding: '6px 12px', fontSize: 13 }} onClick={() => { void saveNowRef.current(); }}>Försök igen</button>
              </>
            )}
          </div>

          {printError && (
            <div className="no-print" role="alert" style={{ color: T.danger, fontSize: 14, marginTop: 12, lineHeight: 1.45 }}>{printError}</div>
          )}

          {!isPrinter && (
            <div className="no-print" style={{ color: T.textMuted, fontSize: 12, marginTop: 12, lineHeight: 1.5 }}>
              Skriv ut i <strong style={{ color: T.textSub }}>faktisk storlek (100 %)</strong> och stäng av sidhuvud och sidfot. Streckade linjer syns bara på skärmen.
            </div>
          )}

          {/* Utskriftskopia direkt under body så flera sidor bryts korrekt */}
          {createPortal(<div id="label-print-root">{renderPrintRoot()}</div>, document.body)}
        </div>
      )}

      {/* ─── Ändra en etikett (vilt) ─────────────────────────────────────── */}
      {editing && editingIdx != null && createPortal(
        <div className="no-print" role="dialog" aria-modal="true" aria-label="Ändra etikett"
          onClick={() => setEditingIdx(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ width: '100%', maxWidth: 520, maxHeight: '92vh', overflowY: 'auto', background: T.surface, color: T.text, borderRadius: '16px 16px 0 0', padding: '16px 16px calc(16px + env(safe-area-inset-bottom))', boxSizing: 'border-box', fontFamily: FONT_UI }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ fontSize: 18, fontWeight: 800 }}>Ändra etikett</div>
              <button type="button" onClick={() => setEditingIdx(null)} style={{ ...btnPrimary, padding: '8px 16px' }}>Klar</button>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
              <LabelCard label={editing} editing onChange={u => handleLabelChange(editingIdx, u)} weightByHand={weightByHand} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={fieldLabel} htmlFor="ed-cut">Styckdetalj</label>
                <select id="ed-cut" value={editing.cut} onChange={e => handleLabelChange(editingIdx, { ...editing, cut: e.target.value })} style={field}>
                  {(cutOptions.includes(editing.cut) ? cutOptions : [...cutOptions, editing.cut]).map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label style={fieldLabel} htmlFor="ed-species">Art</label>
                <select id="ed-species" value={editing.species} onChange={e => handleLabelChange(editingIdx, { ...editing, species: e.target.value })} style={field}>
                  {(VILT_SPECIES.includes(editing.species) ? VILT_SPECIES : [...VILT_SPECIES, editing.species]).map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label style={fieldLabel} htmlFor="ed-styckat">Styckningsdag</label>
                <input id="ed-styckat" type="date" value={editing.styckatDate} onChange={e => handleLabelChange(editingIdx, { ...editing, styckatDate: e.target.value })} style={field} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={fieldLabel} htmlFor="ed-mark">Jaktmark</label>
                <input id="ed-mark" type="text" value={editing.markName} onChange={e => handleLabelChange(editingIdx, { ...editing, markName: e.target.value })} style={field} />
              </div>
              <div>
                <label style={fieldLabel} htmlFor="ed-kgprice">Kilopris (kr/kg)</label>
                <input id="ed-kgprice" type="text" inputMode="decimal" value={editing.kgPrice} placeholder="—" onChange={e => handleLabelChange(editingIdx, { ...editing, kgPrice: e.target.value })} style={field} />
              </div>
              {!weightByHand ? (
                <div>
                  <label style={fieldLabel} htmlFor="ed-weight">Vikt (kg)</label>
                  <input id="ed-weight" type="text" inputMode="decimal" value={editing.weightKg} placeholder="—" onChange={e => handleLabelChange(editingIdx, { ...editing, weightKg: e.target.value })} style={field} />
                </div>
              ) : (
                <div style={{ color: T.textMuted, fontSize: 12, alignSelf: 'end', paddingBottom: 10 }}>Vikten skrivs för hand</div>
              )}
              <div style={{ gridColumn: '1 / -1' }}>
                <PriceInput id="ed-total" label={editing} weightByHand={weightByHand} field={field} fieldLabel={fieldLabel} withLabel
                  onChange={v => handleLabelChange(editingIdx, { ...editing, totalPrice: v })} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={fieldLabel} htmlFor="ed-notes">Anteckning</label>
                <input id="ed-notes" type="text" value={editing.notes} placeholder="T.ex. skytt, kön" onChange={e => handleLabelChange(editingIdx, { ...editing, notes: e.target.value })} style={field} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button type="button" style={{ ...btnSecondary, color: T.danger }} onClick={() => removeLabel(editingIdx)}>Ta bort etiketten</button>
              <button type="button" style={{ ...btnPrimary, flex: 1 }} onClick={() => setEditingIdx(null)}>Klar</button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
